import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DropDownComponent from "./formComponents/DropDownComponent";
import { getTestPerformanceDetails, getTestPerformanceFilterOptions } from "../pages/services/performanceService";
import { renderContentHtml } from "./utils/richText";
import TestScoreSummary from "./TestScoreSummary";
import "../styles/TestPerformance.css";

const EMPTY = { tests: [], batches: [], students: [] };

const TestResultsDetail = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ testId: "", batchId: "", studentId: "", from: "", to: "" });
  const [options, setOptions] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dependencies: Date -> Test, Test -> Batches/Students, Batch -> Students.
  // Changing a parent clears the children it invalidates, otherwise a stale
  // child silently filters the list down to nothing.
  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "from" || key === "to") { next.testId = ""; next.batchId = ""; next.studentId = ""; }
      if (key === "testId") { next.batchId = ""; next.studentId = ""; }
      if (key === "batchId") { next.studentId = ""; }
      return next;
    });
  };

  const optionScope = JSON.stringify({
    testId: filters.testId, batchId: filters.batchId, from: filters.from, to: filters.to,
  });

  useEffect(() => {
    let cancelled = false;
    getTestPerformanceFilterOptions(JSON.parse(optionScope))
      .then((d) => {
        if (cancelled) return;
        setOptions(d);
        // Nothing chosen yet: default to the most recent test, which the API
        // returns first.
        setFilters((prev) => (!prev.testId && d.tests.length ? { ...prev, testId: d.tests[0].value } : prev));
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [optionScope]);

  const scope = JSON.stringify(filters);
  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getTestPerformanceDetails(JSON.parse(scope))
      .then((d) => !cancelled && setDetail(d))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [scope]);

  useEffect(load, [load]);

  const questions = detail?.questions || [];
  const hasActive = filters.testId || filters.batchId || filters.studentId || filters.from || filters.to;

  return (
    <div className="lp-page tp-detail-page">
      <div className="lp-header">
        <div className="lp-header-left">
          <h2 className="lp-title">Detailed Test Results</h2>
          {detail?.test && (
            <span className="lp-count">
              {detail.test.testTitle}
              {detail.test.date ? ` · ${new Date(detail.test.date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}` : ""}
            </span>
          )}
        </div>
        <div className="lp-header-actions">
          <button className="lp-btn-secondary" onClick={() => navigate("/performance-tracking")}>← Back to Performance</button>
        </div>
      </div>

      {/* Filters — every dropdown is searchable (react-select). */}
      <div className="lp-toolbar tp-filter-bar">
        <div className="tp-date-range">
          <label>From<input type="date" value={filters.from} onChange={(e) => handleFilterChange("from", e.target.value)} /></label>
          <label>To<input type="date" value={filters.to} onChange={(e) => handleFilterChange("to", e.target.value)} /></label>
        </div>
        <div className="lp-filters">
          <DropDownComponent
            options={options.tests}
            value={filters.testId}
            onChange={(v) => handleFilterChange("testId", v)}
            placeholder="Test"
            noOptionsMessage="No tests in this date range"
          />
          <DropDownComponent
            options={options.batches}
            value={filters.batchId}
            onChange={(v) => handleFilterChange("batchId", v)}
            placeholder="Batch"
            isDisabled={!options.batches.length}
            noOptionsMessage="No batches for this test"
          />
          <DropDownComponent
            options={options.students}
            value={filters.studentId}
            onChange={(v) => handleFilterChange("studentId", v)}
            placeholder="Student"
            isDisabled={!options.students.length}
            noOptionsMessage={filters.batchId ? "No students in this batch attempted it" : "No students"}
          />
          {hasActive && (
            <button
              type="button"
              className="ql-clear-filters"
              onClick={() => setFilters({ testId: "", batchId: "", studentId: "", from: "", to: "" })}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* The same Test Score Summary as the Test Details page — this screen is
          already scoped to one test, and the per-student scores belong next to
          the per-question breakdown. */}
      {detail?.test?.testId && <TestScoreSummary testId={detail.test.testId} />}

      <div className="lp-table-card tp-questions">
        {loading && <div className="lp-state"><div className="lp-spinner" /><p>Loading results…</p></div>}
        {!loading && error && <div className="lp-state"><p>{error}</p></div>}
        {!loading && !error && !questions.length && (
          <div className="lp-state"><p>No attempts found for this selection.</p></div>
        )}

        {!loading && !error && questions.map((q, i) => (
          <article className="tp-question" key={q.questionId}>
            <header className="tp-q-head">
              <span className="tp-q-num">Q{i + 1}</span>
              <div className="tp-q-text" dangerouslySetInnerHTML={{ __html: renderContentHtml(q.question) }} />
            </header>

            <div className="tp-q-meta">
              <p><strong>Answer:</strong> <span dangerouslySetInnerHTML={{ __html: renderContentHtml(q.correctAnswer) }} /></p>
              <div className="tp-q-solution">
                <strong>Solution:</strong>
                <div dangerouslySetInnerHTML={{ __html: renderContentHtml(q.solution) || "<em>No solution provided.</em>" }} />
              </div>
            </div>

            <table className="tp-answers">
              <thead>
                <tr><th>Student</th><th>Submitted answer</th><th className="tp-result-col">Result</th></tr>
              </thead>
              <tbody>
                {q.students.map((s) => (
                  <tr key={s.studentId}>
                    <td>{s.studentName}</td>
                    <td className={s.attempted ? "" : "tp-unanswered"}>
                      {s.attempted ? s.submittedAnswer : "Not attempted"}
                    </td>
                    <td className="tp-result-col">
                      {s.attempted ? (s.isCorrect ? "✅" : "❌") : <span className="tp-dash">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </article>
        ))}
      </div>
    </div>
  );
};

export default TestResultsDetail;
